export type NavLink = {
  name: string;
  path: string;
};

export type NavGroup = {
  name: string;
  subItems: NavLink[];
};

export type NavEntry = NavLink | NavGroup;

export type NavSectionConfig = {
  id: string;
  label: string;
  entries: NavEntry[];
};

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'subItems' in entry;
}

/** Sidebar sections — setup entities live in their logical category, not a catch-all bucket. */
export const navSections: NavSectionConfig[] = [
  {
    id: 'overview',
    label: 'Overview',
    entries: [{ name: 'Dashboard', path: '/' }],
  },
  {
    id: 'operations',
    label: 'Operations',
    entries: [
      { name: 'Booking Log', path: '/bookings' },
      { name: 'Booking Analytics', path: '/booking-analytics' },
      { name: 'Website Audit', path: '/booking-form-audit' },
      { name: 'Drivers', path: '/drivers' },
      { name: 'Trip Issues', path: '/trip-issues' },
      { name: 'Ratings', path: '/ride-reviews' },
      { name: 'Statements', path: '/statements' },
    ],
  },
  {
    id: 'promotions',
    label: 'Promotions',
    entries: [
      { name: 'Pause Availability', path: '/service-pauses' },
      { name: 'Price Checker', path: '/price-checker' },
      { name: 'Flash Sales', path: '/flash-sales' },
      { name: 'Coupons', path: '/coupons' },
      { name: 'Dead Leg Discounts', path: '/dead-legs' },
      { name: 'Route Fixed Fares', path: '/route-fixed-fares' },
    ],
  },
  {
    id: 'pricing',
    label: 'Pricing',
    entries: [
      { name: 'Per Mile Prices', path: '/per-mile-prices' },
      { name: 'Zones', path: '/zones' },
      { name: 'Location Charges', path: '/location-charges' },
      { name: 'Location Prices', path: '/location-prices' },
      { name: 'Postcode Area Prices', path: '/postcode-area-prices' },
      { name: 'Meet & Greet Charges', path: '/meet-greet-charges' },
      { name: 'Time Prices', path: '/vehicle-time-prices' },
      { name: 'Special Prices', path: '/special-prices' },
      { name: 'Luggage Types', path: '/luggage' },
      { name: 'More Pricing Options', path: '/more-pricing-options' },
    ],
  },
  {
    id: 'availability',
    label: 'Availability',
    entries: [
      { name: 'Number of Vehicles', path: '/number-of-vehicles' },
      { name: 'Notice Periods', path: '/notice-periods' },
      { name: 'Trip Range', path: '/trip-range' },
      { name: 'Operating Hours', path: '/operating-hours' },
    ],
  },
  {
    id: 'fleet',
    label: 'Fleet & Customers',
    entries: [
      { name: 'Vehicle Types', path: '/vehicle-type' },
      { name: 'Vehicles', path: '/vehicles' },
      { name: 'Customers', path: '/customers' },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    entries: [
      { name: 'Website Pages', path: '/website-pages' },
      { name: 'SEO Routes', path: '/seo-routes' },
      { name: 'SEO Places', path: '/seo-places' },
      { name: 'Admin Users', path: '/admin-user' },
      { name: 'Manage Sessions', path: '/manage-sessions' },
      { name: 'Roles', path: '/role' },
      { name: 'Permissions', path: '/permission' },
      {
        name: 'Access Control',
        subItems: [
          { name: 'User Role Mappings', path: '/user-role-mapping' },
          { name: 'Role Permission Mappings', path: '/role-permission-mapping' },
        ],
      },
      { name: 'User Addresses', path: '/admin-user-address' },
    ],
  },
];

/** Match list paths and nested CRUD routes for sidebar highlighting. */
export function isNavPathActive(path: string, pathname: string): boolean {
  const current = pathname.replace(/\/$/, '') || '/';
  const target = path.replace(/\/$/, '') || '/';
  if (current === target) return true;
  if (target !== '/' && current.startsWith(`${target}/`)) return true;
  return false;
}

export function sectionContainsPath(
  section: NavSectionConfig,
  pathname: string,
): boolean {
  return section.entries.some((entry) => {
    if (isNavGroup(entry)) {
      return entry.subItems.some((sub) => isNavPathActive(sub.path, pathname));
    }
    return isNavPathActive(entry.path, pathname);
  });
}

export function collectSectionPaths(section: NavSectionConfig): string[] {
  const paths: string[] = [];
  for (const entry of section.entries) {
    if (isNavGroup(entry)) {
      paths.push(...entry.subItems.map((sub) => sub.path));
    } else {
      paths.push(entry.path);
    }
  }
  return paths;
}
